<template>
  <div class="studio-panel studio-panel-right h-full">
    
    <!-- Panel Header -->
    <div class="panel-header" v-if="!inDrawer">
      <span>{{ $t('Properties') }}</span>
      <el-button v-if="isMobile" circle text @click="$emit('close-mobile')">
        <el-icon><Close /></el-icon>
      </el-button>
    </div>

    <!-- Actions Bar -->
    <div class="p-3 border-b border-gray-100 flex gap-2">
      <el-button type="primary" class="flex-1" @click="$emit('update-chart')">
         <el-icon class="mr-1"><Refresh /></el-icon> {{ $t('Render') }}
      </el-button>
      <el-button plain class="flex-1" @click="$emit('export-layout')">
         <el-icon class="mr-1"><Download /></el-icon> {{ $t('Export') }}
      </el-button>
    </div>

    <div class="panel-content custom-scrollbar">
      
      <!-- Typography -->
      <div class="panel-section">
        <div class="panel-section-title">{{ $t('Typography') }}</div>
        
        <div class="mb-3">
           <label class="text-xs text-gray-500 mb-1 block">{{ $t('Font Family') }}</label>
           <el-select 
              :model-value="selectedFont" 
              @update:model-value="$emit('update:selectedFont', $event)"
              placeholder="Select Font" 
              class="w-full"
              size="default"
              filterable
              popper-class="font-preview-popper"
            >
              <el-option
                v-for="font in fontOptions"
                :key="font.value"
                :label="font.label"
                :value="font.value"
              >
                <div class="font-preview-option">
                  <img
                    :src="getFontPreviewUrl(font.value)"
                    :alt="font.label"
                    class="font-preview-img"
                    loading="lazy"
                    @error="handleFontPreviewError($event, font.label)"
                  />
                </div>
              </el-option>
            </el-select> 
        </div>

        <!-- Future: Orientation / Rotation Controls here -->
      </div>

      <div class="w-full h-px bg-gray-100 my-4"></div>

      <!-- Appearance -->
      <div class="panel-section">
         <div class="panel-section-title">{{ $t('Appearance') }}</div>

         <!-- Color Picker Row -->
         <div class="flex items-center justify-between mb-3 bg-white p-2 rounded border border-gray-100">
            <span class="text-xs text-gray-600">{{ $t('Background Color') }}</span>
            <el-color-picker 
              :model-value="backgroundColor"
              @update:model-value="$emit('update:backgroundColor', $event)" 
              show-alpha 
              :predefine="predefineColors"
              size="small"
            ></el-color-picker>
         </div>

         <!-- Mask -->
         <div class="mb-2">
            <label class="text-xs text-gray-500 mb-1 block">{{ $t('Shape Mask') }}</label>
            <div class="grid grid-cols-2 gap-2">
               <div 
                 class="h-10 rounded border border-gray-200 bg-gray-50 flex items-center justify-center cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition-all text-gray-500"
                 @click="$emit('open-mask-dialog')"
                 :title="$t('Select Template')"
                >
                  <el-icon><Grid /></el-icon>
               </div>
               <div 
                 class="h-10 rounded border border-gray-200 bg-gray-50 flex items-center justify-center cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition-all text-gray-500"
                 @click="selectCustomMask"
                 :title="$t('Upload Custom')"
                >
                   <el-icon><Plus /></el-icon>
               </div>
            </div>
            <input 
              ref="maskFileInput" 
              type="file" 
              accept="image/*" 
              @change="handleMaskFileSelect" 
              style="display: none;"
            >
         </div>
      </div>

      <div class="w-full h-px bg-gray-100 my-4"></div>

      <!-- Canvas Settings -->
      <div class="panel-section">
        <div class="panel-section-title">{{ $t('Dimensions') }}</div>

        <el-select 
            :model-value="selectedSize" 
            @change="$emit('update-image-size', $event)" 
            placeholder="Preset" 
            class="w-full mb-3"
            size="small"
        >
            <el-option
              v-for="size in defaultSizes"
              :key="size.tag"
              :label="size.tag"
              :value="size"
            ></el-option>
        </el-select> 

        <div class="flex items-center gap-3">
           <div class="flex-1 flex items-center">
               <span class="text-xs text-gray-400 mr-2 font-mono">W</span>
               <el-input-number 
                  :model-value="imageWidth" 
                  @update:model-value="$emit('update:imageWidth', $event)"
                  controls-position="right"
                  size="small"
                  class="!w-full"
                  :min="100"
               ></el-input-number>
           </div>
           
           <div class="flex-1 flex items-center">
               <span class="text-xs text-gray-400 mr-2 font-mono">H</span>
               <el-input-number 
                  :model-value="imageHeight" 
                  @update:model-value="$emit('update:imageHeight', $event)"
                  controls-position="right"
                  size="small"
                  class="!w-full"
                  :min="100"
               ></el-input-number>
           </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { ElButton, ElIcon, ElSelect, ElOption, ElColorPicker, ElInputNumber } from 'element-plus';
import { Close, Refresh, Download, Grid, Plus } from '@element-plus/icons-vue';

export default {
  name: 'RightSidebar',
  components: {
    ElButton, ElIcon, ElSelect, ElOption, ElColorPicker, ElInputNumber,
    Close, Refresh, Download, Grid, Plus
  },
  props: {
    isMobile: Boolean,
    selectedFont: String,
    fontOptions: Array,
    backgroundColor: String,
    predefineColors: Array,
    selectedSize: Object,
    defaultSizes: Array,
    imageWidth: Number,
    imageHeight: Number,
    inDrawer: Boolean
  },
  emits: [
    'update-chart', 'export-layout', 'open-mask-dialog', 
    'mask-file-selected', 'update:selectedFont', 'update:backgroundColor', 
    'update-image-size', 'update:imageWidth', 'update:imageHeight', 'close-mobile'
  ],
  methods: {
    selectCustomMask() { this.$refs.maskFileInput.click(); },
    handleMaskFileSelect(event) {
      if (event.target.files?.[0]) {
        this.$emit('mask-file-selected', event.target.files[0]);
      }
      event.target.value = '';
    },
    getFontPreviewUrl(fontValue) {
      // Convert font name to image filename format (spaces to underscores)
      const imageName = fontValue.replace(/ /g, '_') + '.png';
      return `/font_previews/${imageName}`;
    },
    handleFontPreviewError(event, fontLabel) {
      // Fallback: replace broken image with text
      const parent = event.target.parentElement;
      if (parent) {
        parent.innerHTML = `<span style="font-family: ${fontLabel}; font-size: 14px;">${fontLabel}</span>`;
      }
    }
  }
}
</script>

<style scoped>
.font-preview-option {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 24px;
}

.font-preview-img {
  height: 24px;
  width: auto;
  object-fit: contain;
}
</style>

<style>
/* Global styles for the popper (cannot be scoped) */
.font-preview-popper {
  min-width: 280px !important;
}
.font-preview-popper .el-select-dropdown__item {
  height: auto;
  min-height: 32px;
  padding: 4px 12px;
  line-height: normal;
}
</style>
